import { useEffect, useRef, useState } from 'react';
import { Button, Chip, Input, Spinner } from '@nextui-org/react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { maskEmail, maskHandle } from '../lib/mask';
import {
  AUDIT_FILTERS,
  auditDate,
  auditQueryString,
  auditValue,
  describeAudit,
  sensitiveKind
} from '../lib/auditLog';

// Historial de cambios hechos desde el backoffice sobre los usuarios: quién
// (admin), cuándo, a quién y qué cambió. Lo apunta el Worker en cada guardado,
// borrado o desvinculación (GET /api/backoffice/audit, D1 backoffice_audit_log).
//
//   <AuditLog />             buscador completo (Users → Historial)
//   <AuditLog uid="…" />     historial de ese usuario (sin buscador)

const TONE_DOT = {
  success: 'bg-success',
  danger: 'bg-danger',
  warning: 'bg-warning',
  default: 'bg-default-400'
};
const SEARCH_DELAY_MS = 350;

export default function AuditLog({ uid = '', pageSize = 50 }) {
  const { isViewer } = useAuth();
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [tag, setTag] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  // En el buscador: «ver solo a este usuario» (por UID, aunque cambie de correo).
  const [onlyUser, setOnlyUser] = useState(null);
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextBefore, setNextBefore] = useState(null);
  const [missingTable, setMissingTable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(() => new Set());
  const [reload, setReload] = useState(0);
  const request = useRef(0);

  const targetUid = uid || onlyUser?.uid || '';

  useEffect(() => {
    const timer = setTimeout(() => setAppliedSearch(search), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const id = ++request.current;
    setLoading(true);
    setError('');
    apiFetch(`/api/backoffice/audit${auditQueryString({ uid: targetUid, q: uid ? '' : appliedSearch, tag, from, to, limit: pageSize })}`)
      .then((res) => {
        if (id !== request.current) return;
        setItems(res?.items || []);
        setHasMore(!!res?.hasMore);
        setNextBefore(res?.nextBefore || null);
        setMissingTable(!!res?.missingTable);
        setOpen(new Set());
      })
      .catch((e) => {
        if (id !== request.current) return;
        setItems([]);
        setHasMore(false);
        setError(e.message);
      })
      .finally(() => {
        if (id === request.current) setLoading(false);
      });
  }, [uid, targetUid, appliedSearch, tag, from, to, pageSize, reload]);

  async function loadMore() {
    if (!nextBefore || loadingMore) return;
    const id = request.current;
    setLoadingMore(true);
    try {
      const res = await apiFetch(`/api/backoffice/audit${auditQueryString({ uid: targetUid, q: uid ? '' : appliedSearch, tag, from, to, before: nextBefore, limit: pageSize })}`);
      if (id !== request.current) return;
      setItems((list) => [...list, ...(res?.items || [])]);
      setHasMore(!!res?.hasMore);
      setNextBefore(res?.nextBefore || null);
    } catch (e) {
      if (id === request.current) setError(e.message);
    } finally {
      setLoadingMore(false);
    }
  }

  function toggle(id) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Datos personales: el rol viewer los ve enmascarados (como en Users).
  function shownValue(field, value) {
    if (isViewer && typeof value === 'string') {
      const kind = sensitiveKind(field);
      if (kind === 'email' && value.includes('@')) return maskEmail(value);
      if (kind === 'handle') return maskHandle(value) || '—';
    }
    return auditValue(value);
  }

  function targetLabel(item) {
    if (item.targetEmail) return isViewer ? maskEmail(item.targetEmail) : item.targetEmail;
    if (item.targetTiktok) return `@${isViewer ? maskHandle(item.targetTiktok) : item.targetTiktok}`;
    return isViewer ? '********' : item.targetUid;
  }

  const filtered = !!(appliedSearch.trim() || tag !== 'all' || from || to || onlyUser);
  const dateInput = 'h-10 rounded-medium border border-default-200 bg-default-100 px-3 text-sm text-foreground outline-none transition focus:border-primary';

  return (
    <div className="flex flex-col gap-3">
      {!uid && (
        <div className="flex flex-wrap items-end gap-3">
          <Input
            size="sm"
            label="Buscar"
            placeholder="Email, @TikTok o UID del usuario, o email del admin"
            value={search}
            onValueChange={setSearch}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setAppliedSearch(search);
            }}
            isClearable
            className="min-w-[260px] flex-1"
          />
          <label className="flex flex-col gap-1 text-xs text-default-500">
            Desde
            <input type="date" className={dateInput} value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-default-500">
            Hasta
            <input type="date" className={dateInput} value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {AUDIT_FILTERS.filter(([key]) => !uid || key !== 'delete').map(([key, label]) => (
          <Chip
            key={key}
            size="sm"
            variant={tag === key ? 'solid' : 'flat'}
            color={tag === key ? 'primary' : 'default'}
            className="cursor-pointer"
            onClick={() => setTag(key)}
          >
            {label}
          </Chip>
        ))}
        {onlyUser && !uid && (
          <Chip size="sm" variant="flat" color="secondary" onClose={() => setOnlyUser(null)}>
            Solo: {onlyUser.label}
          </Chip>
        )}
        <Button size="sm" variant="light" className="ml-auto" onPress={() => setReload((n) => n + 1)} isDisabled={loading}>
          Refrescar
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">{error}</div>
      )}
      {missingTable && (
        <div className="rounded-md border border-warning-200 bg-warning-50 p-3 text-sm text-warning-700">
          El historial todavía no está activado en el servidor: falta aplicar la migración 022 de la base de datos. Los cambios no se están apuntando.
        </div>
      )}

      {loading ? (
        <div className="flex justify-center p-8"><Spinner label="Cargando historial..." /></div>
      ) : !items.length ? (
        !error && !missingTable && (
          <div className="rounded-md border border-default-200 p-6 text-center text-sm text-default-500">
            {filtered ? 'Ningún cambio coincide con la búsqueda.' : uid ? 'Nadie ha cambiado este usuario desde el backoffice.' : 'Todavía no hay cambios apuntados.'}
            <div className="mt-1 text-xs text-default-400">
              El historial empieza el día en que se activó: los cambios anteriores no están.
            </div>
          </div>
        )
      ) : (
        <div className="overflow-x-auto border-t border-default-200">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b border-default-300 text-[11px] uppercase tracking-wider text-default-500">
                <th className="px-3 py-2 font-semibold">Fecha</th>
                <th className="px-3 py-2 font-semibold">Admin</th>
                {!uid && <th className="px-3 py-2 font-semibold">Usuario</th>}
                <th className="px-3 py-2 font-semibold">Qué hizo</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const lines = describeAudit(item);
                const isOpen = open.has(item.id);
                const deleted = item.action === 'user.delete';
                return (
                  <tr key={item.id} data-audit-row={item.id} className="border-b border-default-200/70 align-top odd:bg-default-100/40 hover:bg-default-200/40">
                    <td className="whitespace-nowrap px-3 py-2 text-default-600">{auditDate(item.ts, { time: true })}</td>
                    <td className="px-3 py-2">
                      <span className="break-all">{item.actorEmail || item.actorUid}</span>
                    </td>
                    {!uid && (
                      <td className="px-3 py-2">
                        <div className="flex min-w-[200px] flex-col">
                          <span className="break-all">{targetLabel(item)}</span>
                          {item.targetEmail && item.targetTiktok && (
                            <span className="text-xs text-default-500">@{isViewer ? maskHandle(item.targetTiktok) : item.targetTiktok}</span>
                          )}
                          <code className="break-all text-xs text-default-400">{isViewer ? '********' : item.targetUid}</code>
                          <div className="mt-1 flex flex-wrap gap-2 text-xs">
                            {onlyUser?.uid !== item.targetUid && (
                              <button type="button" className="text-primary hover:underline" onClick={() => setOnlyUser({ uid: item.targetUid, label: targetLabel(item) })}>
                                Ver su historial
                              </button>
                            )}
                            {!deleted && (
                              <Link className="text-primary hover:underline" to={`/users/${encodeURIComponent(item.targetUid)}`}>Perfil</Link>
                            )}
                          </div>
                        </div>
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-1">
                        {lines.map((line, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <span className={`mt-[7px] h-2 w-2 shrink-0 rounded-full ${TONE_DOT[line.tone] || TONE_DOT.default}`} />
                            <span className={i === 0 ? 'font-medium' : ''}>
                              {line.text}
                              {line.sensitive && (
                                <span className="font-normal text-default-500">
                                  : {shownValue(line.sensitive === 'email' ? 'email' : 'tiktokUsername', line.from)} → {shownValue(line.sensitive === 'email' ? 'email' : 'tiktokUsername', line.to)}
                                </span>
                              )}
                            </span>
                          </div>
                        ))}
                        {item.changes.length > 0 && (
                          <button type="button" className="self-start text-xs text-default-500 underline-offset-2 hover:text-foreground hover:underline" onClick={() => toggle(item.id)}>
                            {isOpen ? 'Ocultar detalle' : deleted ? 'Ver qué tenía' : `Ver ${item.changes.length === 1 ? 'el campo' : `los ${item.changes.length} campos`}`}
                          </button>
                        )}
                        {isOpen && (
                          <div className="mt-1 flex flex-col gap-1 rounded-md border border-default-200 bg-content1 p-2 text-xs">
                            {item.changes.map((change) => {
                              const hadFrom = Object.prototype.hasOwnProperty.call(change, 'from');
                              const hasTo = Object.prototype.hasOwnProperty.call(change, 'to');
                              return (
                                <div key={change.field} className="flex flex-wrap items-baseline gap-x-2">
                                  <code className="text-default-600">{change.field}</code>
                                  {deleted ? (
                                    <span className="break-all">{shownValue(change.field, change.from)}</span>
                                  ) : (
                                    <>
                                      <span className={`break-all ${hadFrom ? 'text-default-500 line-through' : 'text-default-400'}`}>
                                        {hadFrom ? shownValue(change.field, change.from) : 'no existía'}
                                      </span>
                                      <span className="text-default-400">→</span>
                                      <span className={`break-all ${hasTo ? '' : 'text-danger'}`}>
                                        {hasTo ? shownValue(change.field, change.to) : 'campo borrado'}
                                      </span>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {hasMore && !loading && (
        <div className="flex justify-center">
          <Button size="sm" variant="flat" onPress={loadMore} isLoading={loadingMore}>Cargar más</Button>
        </div>
      )}
    </div>
  );
}
